import { useCallback, useEffect, useState } from "react";
import format from "date-fns/format";
import { es } from "date-fns/locale/es";
import { Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useOnlineBookings, refreshOnlineBookings } from "@/lib/onlineBookings";
import { AGENDA } from "@/constants/testIds";

// Fix AR (2026-10-27): "los turnos que se agendan desde la pagina" tienen que verse como reservas online y avisar
// al equipo. Muestra las reservas nuevas de la pagina (las mas recientes primero); al tocar una se abre su turno
// en la agenda y deja de ser nueva. "Marcar como vistas" las saca todas del aviso y del numero del menu.
const MAX_SHOWN = 20;

export default function OnlineBookingsBanner({ onOpen }) {
  const { count, latest } = useOnlineBookings(true);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments/online-new", { params: { items: true } });
      setItems(data.items || []);
    } catch {
      // el aviso es un extra: si falla, la agenda sigue igual
    }
  }, []);

  useEffect(() => {
    if (count > 0) load(); else setItems([]);
  }, [count, latest, load]);

  const markSeen = async (payload) => {
    try {
      await api.post("/appointments/online-new/seen", payload);
    } catch {
      // se reintenta con el proximo toque
    }
    refreshOnlineBookings();
  };

  if (count <= 0) return null;
  return (
    <div className="rounded-xl border border-brand/30 bg-brand-light/50 p-4 space-y-3" data-testid={AGENDA.onlineBanner}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Globe className="w-4 h-4 text-brand shrink-0" />
          {count === 1 ? "1 reserva nueva desde la página" : `${count} reservas nuevas desde la página`}
        </p>
        <Button size="sm" variant="outline" onClick={() => markSeen({ up_to: latest })} data-testid={AGENDA.onlineMarkSeenButton}>
          Marcar como vistas
        </Button>
      </div>
      <ul className="space-y-1">
        {items.slice(0, MAX_SHOWN).map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="w-full text-left text-sm px-3 py-2 rounded-lg bg-white/70 hover:bg-white transition-colors"
              onClick={() => { markSeen({ ids: [item.id] }); onOpen(item); }}
              data-testid={`${AGENDA.onlineItemPrefix}${item.id}`}
            >
              <span className="font-medium">{item.patient_name}</span>
              <span className="text-muted-foreground"> · {item.service_name} · {format(new Date(item.start_time), "EEE d MMM, HH:mm", { locale: es })}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
